"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import "@/components/ui/whatsapp/styles/whatsapp.css";
import { LeadAvatar } from "@/components/LeadAvatar";

interface ChatHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  name: string;
  avatar?: string;
  status?: string | React.ReactNode;
  isOnline?: boolean;
  onVideoCall?: () => void;
  onVoiceCall?: () => void;
  onSearch?: () => void;
  onMenu?: () => void;
  onBack?: () => void;
  onAvatarClick?: () => void;
  onTitleClick?: () => void;
  /** Slot for custom action buttons rendered before the standard actions */
  customActions?: React.ReactNode;
}

function VideoCallIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" height="24" width="24" className={className}>
      <path
        d="M3.27 7.125v9.75c0 .975.825 1.8 1.8 1.8h9c.975 0 1.8-.825 1.8-1.8v-3.375l3.9 3.375V7.125l-3.9 3.375V7.125c0-.975-.825-1.8-1.8-1.8h-9c-.975 0-1.8.825-1.8 1.8z"
        fill="currentColor"
      />
    </svg>
  );
}

function VoiceCallIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" height="24" width="24" className={className}>
      <path
        d="M19.44 13c-.22 0-.45-.07-.67-.12a9.44 9.44 0 0 1-1.31-.39 2 2 0 0 0-2.48 1l-.22.45a12.18 12.18 0 0 1-2.66-2 12.18 12.18 0 0 1-2-2.66l.42-.28a2 2 0 0 0 1-2.48 10.33 10.33 0 0 1-.39-1.31c-.05-.22-.09-.45-.12-.68a2 2 0 0 0-2-1.73H7a2 2 0 0 0-2 2.33 15.64 15.64 0 0 0 13.67 13.67 2 2 0 0 0 2.33-2v-2.1a2 2 0 0 0-1.56-1.7z"
        fill="currentColor"
      />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" height="24" width="24" className={className}>
      <path
        d="M15.009 13.805h-.636l-.22-.219a5.184 5.184 0 0 0 1.256-3.386 5.207 5.207 0 1 0-5.207 5.208 5.183 5.183 0 0 0 3.385-1.255l.221.22v.635l4.004 3.999 1.194-1.195-3.997-4.007zm-4.808 0a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2z"
        fill="currentColor"
      />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" height="24" width="24" className={className}>
      <path
        d="M12 7a2 2 0 1 0-.001-4.001A2 2 0 0 0 12 7zm0 2a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 9zm0 6a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 15z"
        fill="currentColor"
      />
    </svg>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" height="24" width="24" className={className}>
      <path
        d="M12 4l1.4 1.4L7.8 11H20v2H7.8l5.6 5.6L12 20l-8-8z"
        fill="currentColor"
      />
    </svg>
  );
}

const ChatHeader = React.forwardRef<HTMLDivElement, ChatHeaderProps>(
  (
    {
      className,
      name,
      avatar,
      status,
      isOnline = false,
      onVideoCall,
      onVoiceCall,
      onSearch,
      onMenu,
      onBack,
      onAvatarClick,
      onTitleClick,
      customActions,
      ...props
    },
    ref
  ) => {
    const [imgError, setImgError] = React.useState(false);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      if (!menuOpen) return;
      const handlePointerDown = (e: MouseEvent | TouchEvent) => {
        if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
          setMenuOpen(false);
        }
      };
      document.addEventListener("mousedown", handlePointerDown);
      document.addEventListener("touchstart", handlePointerDown);
      return () => {
        document.removeEventListener("mousedown", handlePointerDown);
        document.removeEventListener("touchstart", handlePointerDown);
      };
    }, [menuOpen]);

    return (
      <div
        ref={ref}
        className={cn(
          "font-wa flex h-wa-header-height items-center gap-2 md:gap-[10px] bg-wa-panel-header-bg px-2.5 md:px-4 py-2 relative",
          className
        )}
        {...props}
      >
        {/* Back button (mobile) */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="p-1 text-wa-icon-default transition-colors hover:text-wa-icon-lighter shrink-0 active:scale-95"
            aria-label="Back"
          >
            <BackIcon />
          </button>
        )}

        {/* Avatar */}
        <div className="relative shrink-0 cursor-pointer" onClick={onAvatarClick || onTitleClick}>
          <div className="h-8 w-8 md:h-10 md:w-10 overflow-hidden rounded-full shrink-0">
            {avatar && !imgError ? (
              <img
                src={avatar}
                alt={name}
                className="h-full w-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <LeadAvatar name={name} size="sm" className="h-8 w-8 md:h-10 md:w-10 text-[12px] md:text-[14px]" />
            )}
          </div>
        </div>

        {/* Name & Status */}
        <div className="flex min-w-0 flex-1 cursor-pointer flex-col justify-center overflow-hidden" onClick={onTitleClick}>
          <span className="truncate text-[14px] md:text-[16px] leading-tight md:leading-[21px] font-medium text-wa-text-primary">
            {name}
          </span>
          {(status || isOnline) && (
            <div className="truncate text-[11px] md:text-[13px] leading-tight md:leading-[20px] text-wa-text-secondary">
              {isOnline ? "online" : status}
            </div>
          )}
        </div>

        {/* Desktop Action buttons */}
        <div className="hidden md:flex items-center gap-[10px] shrink-0">
          {customActions}
          {onVideoCall && (
            <button
              type="button"
              onClick={onVideoCall}
              className="rounded-full p-2 text-wa-icon-default transition-colors hover:bg-wa-hover"
              aria-label="Video call"
            >
              <VideoCallIcon />
            </button>
          )}
          {onVoiceCall && (
            <button
              type="button"
              onClick={onVoiceCall}
              className="rounded-full p-2 text-wa-icon-default transition-colors hover:bg-wa-hover"
              aria-label="Voice call"
            >
              <VoiceCallIcon />
            </button>
          )}
          {onSearch && (
            <button
              type="button"
              onClick={onSearch}
              className="rounded-full p-2 text-wa-icon-default transition-colors hover:bg-wa-hover"
              aria-label="Search"
            >
              <SearchIcon />
            </button>
          )}
          {onMenu && (
            <button
              type="button"
              onClick={onMenu}
              className="rounded-full p-2 text-wa-icon-default transition-colors hover:bg-wa-hover"
              aria-label="Menu"
            >
              <MenuIcon />
            </button>
          )}
        </div>

        {/* Mobile Action buttons (< 768px): at most 2 items: Search + Menu (⋮) */}
        <div className="flex md:hidden items-center gap-0.5 shrink-0 relative" ref={menuRef}>
          {onSearch && (
            <button
              type="button"
              onClick={onSearch}
              className="rounded-full p-1.5 text-wa-icon-default hover:bg-wa-hover transition-colors"
              aria-label="Search"
            >
              <SearchIcon className="w-5 h-5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-full p-1.5 text-wa-icon-default hover:bg-wa-hover transition-colors"
            aria-label="Opções"
          >
            <MenuIcon className="w-5 h-5" />
          </button>

          {/* Mobile Dropdown */}
          {menuOpen && (
            <div 
              className="absolute right-0 top-full mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white dark:bg-[#233138] border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl p-3 z-50 flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {customActions && (
                <div className="flex flex-col gap-2 pb-2 border-b border-gray-100 dark:border-gray-800">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Ações da Conversa</div>
                  <div className="flex flex-wrap items-center gap-2">
                    {customActions}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-0.5">
                {onVideoCall && (
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); onVideoCall(); }}
                    className="flex items-center gap-2.5 px-2.5 py-2 text-sm text-wa-text-primary hover:bg-black/5 dark:hover:bg-white/5 rounded-xl transition-colors text-left"
                  >
                    <VideoCallIcon className="w-4 h-4 text-wa-icon-default" />
                    <span>Chamada de vídeo</span>
                  </button>
                )}
                {onVoiceCall && (
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); onVoiceCall(); }}
                    className="flex items-center gap-2.5 px-2.5 py-2 text-sm text-wa-text-primary hover:bg-black/5 dark:hover:bg-white/5 rounded-xl transition-colors text-left"
                  >
                    <VoiceCallIcon className="w-4 h-4 text-wa-icon-default" />
                    <span>Chamada de voz</span>
                  </button>
                )}
                {onMenu && (
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); onMenu(); }}
                    className="flex items-center gap-2.5 px-2.5 py-2 text-sm text-wa-text-primary hover:bg-black/5 dark:hover:bg-white/5 rounded-xl transition-colors text-left"
                  >
                    <MenuIcon className="w-4 h-4 text-wa-icon-default" />
                    <span>Mais opções</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);
ChatHeader.displayName = "ChatHeader";

export { ChatHeader, type ChatHeaderProps };
